import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiPrinter, FiX } from 'react-icons/fi';
import { buildReceiptViewModel } from '../../utils/receiptUtils';
import { RAHBER_LOGO_URL } from '../../config/businessInfo';
import './OrderReceiptModal.css';

const OrderReceiptModal = ({
    isOpen,
    onClose,
    order,
    fallbackContext,
    autoPrintKey = 0,
}) => {
    const [logoFailed, setLogoFailed] = useState(false);
    const logoImgRef = useRef(null);
    const lastPrintedKeyRef = useRef(0);

    const receipt = useMemo(
        () => buildReceiptViewModel(order, fallbackContext),
        [order, fallbackContext]
    );

    // Preload company logo into browser cache as soon as an order exists
    useEffect(() => {
        if (!order && !fallbackContext?.product) return;
        const preloadImg = new Image();
        preloadImg.src = RAHBER_LOGO_URL;
    }, [order, fallbackContext]);

    const executeBrowserPrint = useCallback(() => {
        if (!receipt) return;

        const previousTitle = document.title;
        document.title = `Order Receipt - ${receipt.orderNumber} - Rahber Corporation`;

        let restored = false;
        const restoreTitle = () => {
            if (restored) return;
            restored = true;
            document.title = previousTitle;
            window.removeEventListener('afterprint', restoreTitle);
        };

        window.addEventListener('afterprint', restoreTitle);

        const trigger = () => {
            window.print();
            // Fallback title restoration if browser does not fire afterprint immediately
            setTimeout(restoreTitle, 2500);
        };

        const imgEl = logoImgRef.current;
        if (!imgEl || logoFailed || imgEl.complete) {
            requestAnimationFrame(() => {
                setTimeout(trigger, 60);
            });
            return;
        }

        let printed = false;
        const runOnce = () => {
            if (printed) return;
            printed = true;
            trigger();
        };

        imgEl.addEventListener('load', runOnce, { once: true });
        imgEl.addEventListener('error', runOnce, { once: true });
        setTimeout(runOnce, 500);
    }, [receipt, logoFailed]);

    // Toggle body print-isolation class while receipt modal is open
    useEffect(() => {
        if (!isOpen || !receipt) return undefined;

        document.body.classList.add('rahber-receipt-ready');

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.body.classList.remove('rahber-receipt-ready');
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isOpen, receipt, onClose]);

    // Automatically invoke print dialog when triggered via "Print Receipt" button
    useEffect(() => {
        if (!isOpen || !receipt || !autoPrintKey) return;
        if (lastPrintedKeyRef.current === autoPrintKey) return;
        lastPrintedKeyRef.current = autoPrintKey;
        executeBrowserPrint();
    }, [isOpen, receipt, autoPrintKey, executeBrowserPrint]);

    if (!isOpen || !receipt || typeof document === 'undefined') {
        return null;
    }

    const { company, customer, delivery, items, summary } = receipt;

    return createPortal(
        <div
            id="rahber-order-receipt-portal"
            role="dialog"
            aria-modal="true"
            aria-label={`Order Receipt ${receipt.orderNumberDisplay}`}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            {/* On-Screen Control Bar (Automatically hidden when printing) */}
            <div className="rahber-receipt-toolbar receipt-no-print">
                <div className="rahber-receipt-toolbar__left">
                    <span className="rahber-receipt-toolbar__title">
                        Official Order Receipt ({receipt.orderNumberDisplay})
                    </span>
                    <span className="rahber-receipt-toolbar__hint">
                        A4 print-ready copy · Select your printer or choose &ldquo;Save as PDF&rdquo;
                    </span>
                </div>
                <div className="rahber-receipt-toolbar__actions">
                    <button
                        type="button"
                        className="rahber-receipt-btn rahber-receipt-btn--primary"
                        onClick={executeBrowserPrint}
                    >
                        <FiPrinter aria-hidden="true" />
                        <span>Print Receipt</span>
                    </button>
                    <button
                        type="button"
                        className="rahber-receipt-btn rahber-receipt-btn--close"
                        onClick={onClose}
                        aria-label="Close receipt preview"
                    >
                        <FiX aria-hidden="true" />
                        <span>Close</span>
                    </button>
                </div>
            </div>

            {/* Printable A4 Order Receipt Sheet */}
            <article className="rahber-receipt-sheet">
                {/* 1. Company Branding & Order Metadata Header */}
                <header className="rahber-receipt-header">
                    <div className="rahber-receipt-brand">
                        <div className="rahber-receipt-logo-row">
                            {!logoFailed && (
                                <img
                                    ref={logoImgRef}
                                    src={company.logoUrl}
                                    alt={company.name}
                                    className="rahber-receipt-logo"
                                    loading="eager"
                                    onError={() => setLogoFailed(true)}
                                />
                            )}
                        </div>
                        <h1 className="rahber-receipt-company-name">{company.name}</h1>
                        <p className="rahber-receipt-company-tagline">{company.tagline}</p>
                        <p className="rahber-receipt-company-contact">
                            {company.address}
                            <br />
                            Tel: {company.phones} · Email: {company.email}
                        </p>
                    </div>

                    <div className="rahber-receipt-doc-meta">
                        <h2 className="rahber-receipt-doc-heading">Order Receipt</h2>
                        <div className="rahber-receipt-meta-list">
                            <div className="rahber-receipt-meta-row">
                                <span className="rahber-receipt-meta-label">Order No:</span>
                                <span className="rahber-receipt-meta-value rahber-receipt-meta-value--order-id">
                                    {receipt.orderNumberDisplay}
                                </span>
                            </div>
                            <div className="rahber-receipt-meta-row">
                                <span className="rahber-receipt-meta-label">Order Date:</span>
                                <span className="rahber-receipt-meta-value">{receipt.fullDateTime}</span>
                            </div>
                            <div className="rahber-receipt-meta-row">
                                <span className="rahber-receipt-meta-label">Order Status:</span>
                                <span className="rahber-receipt-meta-value rahber-receipt-meta-value--status">
                                    {receipt.orderStatus}
                                </span>
                            </div>
                            <div className="rahber-receipt-meta-row">
                                <span className="rahber-receipt-meta-label">Order Type:</span>
                                <span className="rahber-receipt-meta-value">{receipt.orderTypeLabel}</span>
                            </div>
                        </div>
                    </div>
                </header>

                {/* 2. Customer & Delivery Information */}
                <section className="rahber-receipt-info-grid" aria-label="Customer and delivery information">
                    <div className="rahber-receipt-info-col">
                        <div className="rahber-receipt-section-kicker">Customer Information</div>
                        <div className="rahber-receipt-field-row">
                            <span className="rahber-receipt-field-key">Customer Name:</span>
                            <span className="rahber-receipt-field-val rahber-receipt-field-val--strong">
                                {customer.name}
                            </span>
                        </div>
                        {customer.phone && (
                            <div className="rahber-receipt-field-row">
                                <span className="rahber-receipt-field-key">Phone Number:</span>
                                <span className="rahber-receipt-field-val">{customer.phone}</span>
                            </div>
                        )}
                        {customer.whatsapp && (
                            <div className="rahber-receipt-field-row">
                                <span className="rahber-receipt-field-key">WhatsApp:</span>
                                <span className="rahber-receipt-field-val">{customer.whatsapp}</span>
                            </div>
                        )}
                        {customer.email && (
                            <div className="rahber-receipt-field-row">
                                <span className="rahber-receipt-field-key">Email:</span>
                                <span className="rahber-receipt-field-val">{customer.email}</span>
                            </div>
                        )}
                    </div>

                    <div className="rahber-receipt-info-col">
                        <div className="rahber-receipt-section-kicker">Delivery &amp; Payment Details</div>
                        {delivery.address && (
                            <div className="rahber-receipt-field-row">
                                <span className="rahber-receipt-field-key">Delivery Address:</span>
                                <span className="rahber-receipt-field-val">{delivery.address}</span>
                            </div>
                        )}
                        <div className="rahber-receipt-field-row">
                            <span className="rahber-receipt-field-key">Payment Method:</span>
                            <span className="rahber-receipt-field-val">{receipt.paymentMethod}</span>
                        </div>
                        {delivery.schedule && (
                            <div className="rahber-receipt-field-row">
                                <span className="rahber-receipt-field-key">Delivery Slot:</span>
                                <span className="rahber-receipt-field-val">{delivery.schedule}</span>
                            </div>
                        )}
                        {delivery.notes && (
                            <div className="rahber-receipt-field-row">
                                <span className="rahber-receipt-field-key">Instructions:</span>
                                <span className="rahber-receipt-field-val">{delivery.notes}</span>
                            </div>
                        )}
                    </div>
                </section>

                {/* 3. Purchased Products Table */}
                <section className="rahber-receipt-items-section" aria-label="Purchased products">
                    <div className="rahber-receipt-table-wrap">
                        <table className="rahber-receipt-table">
                            <thead>
                                <tr>
                                    <th className="rahber-receipt-col-idx">#</th>
                                    <th>Product Description</th>
                                    <th className="rahber-receipt-col-qty">Qty</th>
                                    <th className="rahber-receipt-col-price">Unit Price</th>
                                    <th className="rahber-receipt-col-discount">Discount</th>
                                    <th className="rahber-receipt-col-total">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => {
                                    const metaParts = [];
                                    if (item.brand) metaParts.push(`Brand: ${item.brand}`);
                                    if (item.sku) metaParts.push(`Model/SKU: ${item.sku}`);

                                    return (
                                        <tr key={`${item.index}-${item.name}`}>
                                            <td className="rahber-receipt-col-idx">{item.index}</td>
                                            <td>
                                                <div className="rahber-receipt-item-name">{item.name}</div>
                                                {metaParts.length > 0 && (
                                                    <div className="rahber-receipt-item-sub">
                                                        {metaParts.join(' · ')}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="rahber-receipt-col-qty">{item.quantity}</td>
                                            <td className="rahber-receipt-col-price">
                                                {item.unitPriceFormatted}
                                            </td>
                                            <td
                                                className={`rahber-receipt-col-discount${
                                                    item.discountAmount > 0
                                                        ? ''
                                                        : ' rahber-receipt-col-discount--none'
                                                }`}
                                            >
                                                {item.discountFormatted}
                                                {item.discountPercent > 0 && (
                                                    <span className="rahber-receipt-discount-sub">
                                                        ({item.discountPercent}% off)
                                                    </span>
                                                )}
                                            </td>
                                            <td className="rahber-receipt-col-total">
                                                {item.lineTotalFormatted}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* 4. Installment Schedule / Showroom Terms + Complete Payment Breakdown */}
                <section className="rahber-receipt-bottom-grid" aria-label="Payment summary">
                    <div className="rahber-receipt-notes-stack">
                        {receipt.isInstallmentOrder && (
                            <div className="rahber-receipt-note-card rahber-receipt-note-card--installment">
                                <h3 className="rahber-receipt-note-title">Installment Schedule Summary</h3>
                                <ul className="rahber-receipt-note-list">
                                    <li>
                                        Plan Type: <strong>{receipt.orderTypeLabel}</strong>
                                        {summary.installmentMonths
                                            ? ` (${summary.installmentMonths} Months)`
                                            : ''}
                                    </li>
                                    <li>
                                        Upfront Payment:{' '}
                                        <strong>{summary.advanceAmountFormatted}</strong>
                                        {summary.downPaymentPercent !== null &&
                                        summary.downPaymentPercent !== undefined
                                            ? ` (${summary.downPaymentPercent}% advance)`
                                            : ''}
                                    </li>
                                    {summary.monthlyInstallment > 0 && (
                                        <li>
                                            Monthly Installment:{' '}
                                            <strong>
                                                {summary.monthlyInstallmentFormatted} / month
                                            </strong>
                                            {summary.remainingInstallmentCount
                                                ? ` × ${summary.remainingInstallmentCount} months`
                                                : ''}
                                        </li>
                                    )}
                                    <li>
                                        Verification: Subject to standard showroom verification (valid CNIC &amp; 2 Lahore guarantors).
                                    </li>
                                </ul>
                            </div>
                        )}

                        <div className="rahber-receipt-note-card">
                            <h3 className="rahber-receipt-note-title">Warranty &amp; Dispatch Assurance</h3>
                            <ul className="rahber-receipt-note-list">
                                <li>
                                    100% factory-sealed genuine appliance accompanied by the official stamped manufacturer warranty card.
                                </li>
                                <li>
                                    Our showroom dispatch team ({company.primaryHelpline}) verifies order &amp; delivery schedule prior to dispatch.
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="rahber-receipt-summary-box">
                        <div className="rahber-receipt-section-kicker">Payment Summary (PKR)</div>

                        <div className="rahber-receipt-summary-row">
                            <span className="rahber-receipt-summary-label">
                                Subtotal ({receipt.totalQuantity}{' '}
                                {receipt.totalQuantity === 1 ? 'item' : 'items'})
                            </span>
                            <span className="rahber-receipt-summary-val">
                                {summary.subtotalFormatted}
                            </span>
                        </div>

                        <div className="rahber-receipt-summary-row">
                            <span className="rahber-receipt-summary-label">Discount</span>
                            <span
                                className={`rahber-receipt-summary-val${
                                    summary.discountAmount > 0
                                        ? ' rahber-receipt-summary-val--discount'
                                        : ''
                                }`}
                            >
                                {summary.discountFormatted}
                            </span>
                        </div>

                        {receipt.isInstallmentOrder && (
                            <div className="rahber-receipt-summary-row">
                                <span className="rahber-receipt-summary-label">
                                    Installment Markup
                                    {summary.markupRate !== null && summary.markupRate !== undefined
                                        ? ` (${summary.markupRate}%)`
                                        : ''}
                                </span>
                                <span className="rahber-receipt-summary-val">
                                    {summary.markupFormatted}
                                </span>
                            </div>
                        )}

                        <div className="rahber-receipt-summary-row">
                            <span className="rahber-receipt-summary-label">Delivery Charges</span>
                            <span className="rahber-receipt-summary-val">
                                {summary.deliveryChargesLabel}
                            </span>
                        </div>

                        <div className="rahber-receipt-summary-total">
                            <span className="rahber-receipt-summary-total__label">
                                Total Order Amount
                            </span>
                            <span className="rahber-receipt-summary-total__val">
                                {summary.totalOrderAmountFormatted}
                            </span>
                        </div>

                        {receipt.isInstallmentOrder && (
                            <div className="rahber-receipt-installment-settlement">
                                <div className="rahber-receipt-settlement-row rahber-receipt-settlement-row--advance">
                                    <span className="rahber-receipt-summary-label">
                                        {summary.advanceLabel}
                                    </span>
                                    <span className="rahber-receipt-summary-val">
                                        {summary.advanceAmountFormatted}
                                    </span>
                                </div>
                                <div className="rahber-receipt-settlement-row rahber-receipt-settlement-row--balance">
                                    <span className="rahber-receipt-summary-label">
                                        Remaining Balance
                                    </span>
                                    <span className="rahber-receipt-summary-val">
                                        {summary.remainingBalanceFormatted}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* 5. Official Footer */}
                <footer className="rahber-receipt-footer">
                    <div>
                        <strong>Thank you for choosing {company.name}.</strong>
                        <div>
                            Computer-generated official order receipt. Please keep this receipt for delivery verification and showroom records.
                        </div>
                    </div>
                    <div className="rahber-receipt-footer__right">
                        {company.website} · Helpline: {company.primaryHelpline}
                    </div>
                </footer>
            </article>
        </div>,
        document.body
    );
};

export default OrderReceiptModal;
